import { CallHistoryMethodAction, push, RouterAction } from 'connected-react-router';
import { AnyAction } from 'redux';
import { Country } from '~/Api/Country';
import {
	findProduct,
	findProducts,
	ProductErrorResponse,
	ProductResult,
	ProductsErrorResponse,
	ProductsResult,
	Query,
	validateQuery as apiValidateQuery,
} from '~/Api/Product';
import { invokeApi, invokeWithRetry, resolve } from '~/ApiClient';
import { handleError } from '~/ErrorHandler';
import { flashWarningMessage, removeFlashMessageByText } from '~/FlashMessage/actions';
import { logGoogleTagManagerSearch, logGoogleTagManagerSearchResponse } from '~/GoogleTagManager';
import FilterParameter from '~/utils/FilterParameter/FilterParameter';
import buildProductSearchUrl from '~/Router/buildProductSearchUrl';
import { ThunkAction } from '~/Store';

import { Action, ActionType } from './Action';
import { selectProductById, selectProductsByQuery } from './reducer';

const SlowSearchMessage = 'Searching is taking unusually long time, please be patient';
let cancelCurrentProductsFetch = () => {};

export function fetchProducts(
	query: Query,
	page = 1,
	perPage = 100
): ThunkAction<Promise<ProductsResult | null>, Action> {
	return async (dispatch) => {
		cancelCurrentProductsFetch();

		const [request, cancel] = invokeWithRetry(
			() => dispatch(invokeApi(findProducts, processQuery(query), page, perPage)),
			59 * 1000
		);
		cancelCurrentProductsFetch = cancel;

		dispatch({ type: ActionType.requestProducts, request, query });

		return resolve(request, (response, error) => {
			const paginationLimitReached =
				error && error instanceof ProductsErrorResponse ? error.handlePaginationLimit() : false;
			const queryErrors = error ? (error instanceof ProductsErrorResponse ? error.handleQueryErrors() : {}) : null;

			dispatch(
				response !== null
					? {
							type: ActionType.receiveProducts,
							request,
							query,
							items: response.items,
							pagination: response.pagination,
							paginationLimitReached,
							stats: response.stats,
							queryErrors,
					  }
					: {
							type: ActionType.receiveProducts,
							request,
							query,
							items: null,
							pagination: null,
							paginationLimitReached,
							stats: null,
							queryErrors,
					  }
			);
		});
	};
}

export function processQuery(query: Query): Query {
	return query.filterParametersUpdated(processFilterParameters(query.filterParameters));
}

function processFilterParameters(
	filterParameters: FilterParameter<string | string[]>[]
): FilterParameter<string | string[]>[] {
	return filterParameters.map((parameter) => {
		if (parameter.name === 'atc' && typeof parameter.value === 'string') {
			return parameter.valueUpdated(parameter.value.toUpperCase());
		}
		return parameter;
	});
}

export function fetchMoreProducts(query: Query): ThunkAction<Promise<ProductsResult | null>, Action> {
	return async (dispatch, getState) => {
		const products = selectProductsByQuery(getState().products, query);
		if (products === null || products.pagination === null) {
			return dispatch(fetchProducts(query));
		}

		const nextPage = products.pagination?.nextGrowedPage ?? null;
		if (nextPage === null) {
			return null;
		}

		return dispatch(fetchProducts(query, nextPage.number, nextPage.perPage));
	};
}

export function loggedFetchMoreProducts(query: Query): ThunkAction<Promise<ProductsResult | null>, AnyAction> {
	return async (dispatch, getState) => {
		const time = performance.now();
		const slowMessageTimeoutId = setTimeout(() => {
			if (selectProductsByQuery(getState().products, query)) {
				dispatch(flashWarningMessage(SlowSearchMessage, false));
			}
		}, 5 * 1000);
		const { availableParameters } = getState().products;

		logGoogleTagManagerSearch(query.filterParameters, availableParameters, query.sortField, query.order);

		try {
			const response = await dispatch(fetchMoreProducts(query));
			clearTimeout(slowMessageTimeoutId);
			dispatch(removeFlashMessageByText(SlowSearchMessage));
			logGoogleTagManagerSearchResponse(
				query.filterParameters,
				availableParameters,
				query.operator,
				query.sortField,
				query.order,
				performance.now() - time,
				response?.pagination.total ?? 0
			);
			return response;
		} catch (error) {
			clearTimeout(slowMessageTimeoutId);
			dispatch(removeFlashMessageByText(SlowSearchMessage));
			throw error;
		}
	};
}

export function showAllProductsAction(showAll: boolean): Action {
	return { type: ActionType.showAllProductsAction, showAll };
}

export function pruneProducts(query: Query): Action {
	cancelCurrentProductsFetch();
	return { type: ActionType.pruneProducts, query };
}

export function validateQuery(query: Query, countries: Country[]): ThunkAction<boolean, Action> {
	return (dispatch) => {
		const errors = apiValidateQuery(query, countries);
		dispatch({ type: ActionType.setQueryErrors, query, errors });
		return Object.keys(errors).length === 0;
	};
}

export function fetchProduct(id: string): ThunkAction<Promise<ProductResult | null>, Action> {
	return async (dispatch) => {
		const request = dispatch(invokeApi(findProduct, id));
		dispatch({ type: ActionType.requestProduct, request, id });

		try {
			const response = await request;
			dispatch({
				type: ActionType.receiveProduct,
				request,
				id,
				product: response.product,
			});
			return response;
		} catch (error) {
			dispatch({
				type: ActionType.receiveProduct,
				request,
				id,
				product: error instanceof ProductErrorResponse && error.handleProductNotFound() ? null : undefined,
			});
			dispatch(handleError(error));
			return null;
		}
	};
}

export function fetchProductIfNeeded(id: string): ThunkAction<Promise<ProductResult | null>, Action> {
	return async (dispatch, getState) => {
		const { products } = getState();
		const { productRequest } = products;
		if (productRequest?.id === id) {
			return productRequest.request;
		}

		const product = selectProductById(products, id);
		if (product === null) {
			return null;
		}
		if (product !== undefined) {
			return { product };
		}

		return dispatch(fetchProduct(id));
	};
}

export function redirectToSearch(
	query: Query,
	productId?: string | null,
	priceType?: string | null,
	currency?: string | null,
	filterCalculatedPrice?: boolean
): ThunkAction<CallHistoryMethodAction, Action | RouterAction> {
	return (dispatch, getState) => {
		return dispatch(
			push(
				buildProductSearchUrl(
					query,
					productId,
					priceType !== undefined ? priceType : getState().productPrice.priceTypePreference.load(),
					currency,
					filterCalculatedPrice
				)
			)
		);
	};
}
